<script setup lang="ts">

import {useShipActionStore} from "@/stores/shipActionStore.ts";
import Backend from '@/models/backend.ts'
import { useFleetStore } from '@/stores/fleetStore.ts'
import { useShipStore } from '@/stores/shipStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import { useRouteStore } from '@/stores/routeStore.ts'
import { useRouteEditorStore } from '@/stores/routeEditorStore.ts'
import { CDN } from '@/models/cdn.ts'
import DeleteButton from '@/components/Elements/Buttons/DeleteButton.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import FormSelect from '@/components/Elements/Forms/FormSelect.vue'
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores / models
 */
const shipActionStore = useShipActionStore()
const fleetStore = useFleetStore()
const shipStore = useShipStore()
const gameStore = useGameStore()
const mapStore = useMapStore()
const routeStore = useRouteStore()
const routeEditorStore = useRouteEditorStore()
const route = useRoute()

// Track if actions are being submitted
const isSubmitting = ref(false)

// Get all routes for the dropdown
const allRoutes = computed(() => routeStore.getAllRoutes)

const routeOptions = computed(() => [
  { value: 0, label: t('map.commands.selectRoute') },
  ...allRoutes.value.map((r) => ({ value: r.id, label: `${r.name} (${t('map.commands.waypoints', { count: r.waypoints.length })})` }))
])
const hasRoutes = computed(() => routeStore.getRouteCount > 0)

/**
 * Functions
 */
const resetAction = () => {
  shipActionStore.reset()
}

const undoLastAction = () => {
  shipActionStore.setPendingAction('')
  mapStore.shipActionPath.removeLastAction()
}

const setActions = () => {
  isSubmitting.value = true
  const backend = new Backend()
  if (shipActionStore.fleetId > 0) {
    //Store fleet orders
    backend.setFleetActions(shipActionStore.fleetId, shipActionStore.actions)
      .then(() => {
        gameStore.setTinySuccessToastMessage(t('map.commands.ordersSetForFleet', { name: fleetStore.getFleetNameById(shipActionStore.fleetId) }))
        shipActionStore.reset()
      })
      .catch((error) => {
      gameStore.setTinyErrorToastMessage(error.response.data.message)
      shipActionStore.reset()
    })
      .finally(() => {
        isSubmitting.value = false
      })
  } else {
    //Store ship orders
    backend.setShipActions(shipActionStore.ships, shipActionStore.actions)
      .then(() => {
        gameStore.setTinySuccessToastMessage(t('map.commands.ordersSetForShips', { count: shipActionStore.ships.length }))
        shipActionStore.reset()
      })
      .catch((error) => {
      gameStore.setTinyErrorToastMessage(error.response.data.message)
      shipActionStore.reset()
    })
      .finally(() => {
        isSubmitting.value = false
      })
  }
}

const queueExplore = () => {
  shipActionStore.setPendingAction('explore')
  shipActionStore.queuePendingAction(
    0,
    0,
    0,
    0
  );
  setActions()
}

const queueRepair = () => {
  // Open repair action configuration modal
  gameStore.modalType = 'repairAction'
}

const queueWait = () => {
  // Open wait action configuration modal
  gameStore.modalType = 'waitAction'
}

const queueDefend = () => {
  shipActionStore.setPendingAction('defend')
  
  // Use the position where the last command ended if there are previous commands,
  // otherwise use the current position
  const x = shipActionStore.actions.length > 0 ? shipActionStore.pathEndX : shipActionStore.pathStartX
  const y = shipActionStore.actions.length > 0 ? shipActionStore.pathEndY : shipActionStore.pathStartY
  
  shipActionStore.queuePendingAction(
    x,
    y,
    0,
    0
  );
  setActions()
}

const detachShipsFromFleet = () => {
  const ids = []; // Use a different variable name to avoid redeclaration confusion
  for (const ship of shipStore.ships.values()) {
    if (
      Number(ship.xPos) >= Number(mapStore.areaBoxTL.x) &&
      Number(ship.xPos) <= Number(mapStore.areaBoxRB.x) &&
      Number(ship.yPos) <= Number(mapStore.areaBoxTL.y) &&
      Number(ship.yPos) >= Number(mapStore.areaBoxRB.y)
    ) {
      if (ship.fleetId === shipActionStore.fleetId) {
        ids.push(ship.id);
      }
    }
  }

  const backend = new Backend()
  backend.detachShipsFromFleet(ids)

  shipActionStore.reset()
  gameStore.setTinySuccessToastMessage(t('map.commands.shipsDetached', { count: ids.length }))
}

const enterPatrolMode = () => {
  shipActionStore.setPatrolMode(true, 'patrol')
}

const enterPatrolDefendMode = () => {
  shipActionStore.setPatrolMode(true, 'patrolDefend')
}

const cancelPatrol = () => {
  shipActionStore.setPatrolMode(false)
}

const addNewRoute = () => {
  routeEditorStore.startNewRoute()
}

const setPatrolAndSubmit = () => {
  shipActionStore.queuePatrolAction()
  setActions()
}

// Watch for new route creation to auto-select it
watch(() => routeStore.routes.size, (newSize, oldSize) => {
  if (newSize > oldSize && shipActionStore.patrolMode && routeEditorStore.pendingNewRoute) {
    // A new route was added while in patrol mode, find the newest one
    const routes = routeStore.getAllRoutes
    // The newest route is likely the one with the highest ID
    const newestRoute = routes.reduce((prev, current) =>
      (prev.id > current.id) ? prev : current
    )
    if (newestRoute) {
      shipActionStore.setSelectedRouteId(newestRoute.id)
      routeEditorStore.setLastCreatedRouteId(newestRoute.id)
    }
  }
})

/**
 * Event listeners
 */
const handleKeyDown = (event: KeyboardEvent) => {
  // Only handle keyboard shortcuts when on the map view
  if(!route.name?.toString().startsWith('MapGalaxy')) {
    return
  }

  // Don't handle shortcuts when user is typing in an input field
  const activeElement = document.activeElement
  if (activeElement && (
    activeElement.tagName === 'INPUT' ||
    activeElement.tagName === 'TEXTAREA' ||
    (activeElement as HTMLElement).isContentEditable
  )) {
    return
  }

  if(shipActionStore.initialized) {
    if (event.key === 'm' || event.key === 'M') {
      shipActionStore.setPendingAction('move')
    } else if(event.key === 'a' || event.key === 'A') {
      shipActionStore.setPendingAction('attack')
    } else if(event.key === 's' || event.key === 'S') {
      shipActionStore.setPendingAction('settle')
    } else if (event.key === 'f' || event.key === 'F') {
      shipActionStore.setPendingAction('follow')
    } else if (event.key === 't' || event.key === 'T') {
      shipActionStore.setPendingAction('followAttack')
    } else if (event.key === 'r' || event.key === 'R') {
      queueRepair()
    } else if (event.key === 'e' || event.key === 'E') {
      queueExplore()
    } else if (event.key === 'l' || event.key === 'L') {
      detachShipsFromFleet()
    } else if (event.key === 'd' || event.key === 'D') {
      queueDefend()
    } else if (event.key === 'w' || event.key === 'W') {
      queueWait()
    } else if (event.key === 'P' && event.shiftKey) {
      enterPatrolDefendMode()
    } else if (event.key === 'p' || event.key === 'P') {
      enterPatrolMode()
    }
    //esc
    if (event.key === 'Escape') {
      shipActionStore.reset();
    }
    //del
    if (event.key === 'Delete' || event.key === 'Backspace') {
      shipActionStore.setPendingAction('')
      mapStore.shipActionPath.removeLastAction()
    }
  }
  if(mapStore.areaActionSelecting) {
    if (event.key === 'Escape') {
      mapStore.disableAreaSelection();
    }
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
})

</script>

<template>
<div v-if="shipActionStore.initialized" class="absolute w-full top-0 text-center flex justify-center pointer-events-none">
  <div class="flex flex-col bg-gradient-to-b from-metal-800 to-metal-950 p-2 pointer-events-auto rounded-b text-metal-300">
    <div v-if="shipActionStore.fleetId === 0">{{ t('map.commands.shipsSelected', { count: shipActionStore.ships.length }) }}</div>
    <div v-else>{{ t('map.commands.fleetSelected', { name: fleetStore.getFleetNameById(Number(shipActionStore.fleetId)) }) }}</div>

    <!-- Command buttons (hidden when in patrol mode) -->
    <div v-if="!shipActionStore.patrolMode" class="flex flex-wrap justify-center md:flex-nowrap">
      <button
        class="cmd-btn"
        :class="{ 'active': shipActionStore.pendingAction === 'move' }"
        @click="shipActionStore.setPendingAction('move')"
      >
        <img :src="CDN.icon32('cmd_move')" :alt="t('map.commands.move')" :title="t('map.commands.move')"/>
      </button>
      <button
        class="cmd-btn"
        :class="{ 'active': shipActionStore.pendingAction === 'attack' }"
        @click="shipActionStore.setPendingAction('attack')"
      >
        <img :src="CDN.icon32('cmd_attack')" :alt="t('map.commands.attack')" :title="t('map.commands.attack')"/>
      </button>
      <button
        class="cmd-btn"
        :class="{ 'active': shipActionStore.pendingAction === 'settle' }"
        @click="shipActionStore.setPendingAction('settle')"
      >
        <img :src="CDN.icon32('cmd_settle')" :alt="t('map.commands.settle')" :title="t('map.commands.settle')"/>
      </button>
      <button
        class="cmd-btn"
        :class="{ 'active': shipActionStore.pendingAction === 'follow' }"
        @click="shipActionStore.setPendingAction('follow')"
      >
        <img :src="CDN.icon32('cmd_follow')" :alt="t('map.commands.follow')" :title="t('map.commands.follow')"/>
      </button>
      <button
        class="cmd-btn"
        :class="{ 'active': shipActionStore.pendingAction === 'followAttack' }"
        @click="shipActionStore.setPendingAction('followAttack')"
      >
        <img :src="CDN.icon32('cmd_followattack')" :alt="t('map.commands.followAttack')" :title="t('map.commands.followAttack')"/>
      </button>
      <button
        v-if="shipActionStore.fleetId === 0"
        class="cmd-btn"
        :class="{ 'active': shipActionStore.pendingAction === 'explore' }"
        @click="queueExplore()"
      >
        <img :src="CDN.icon32('cmd_search')" :alt="t('map.commands.explore')" :title="t('map.commands.explore')"/>
      </button>
      <button
        class="cmd-btn"
        :class="{ 'active': shipActionStore.pendingAction === 'repair' }"
        @click="queueRepair()"
      >
        <img :src="CDN.icon32('cmd_repair')" :alt="t('map.commands.repair')" :title="t('map.commands.repair')"/>
      </button>
      <button
        class="cmd-btn"
        :class="{ 'active': shipActionStore.pendingAction === 'defend' }"
        @click="queueDefend()"
      >
        <img :src="CDN.icon32('shields')" :alt="t('map.commands.defend')" :title="t('map.commands.defend')"/>
      </button>
      <button
        class="cmd-btn"
        :class="{ 'active': shipActionStore.pendingAction === 'wait' }"
        @click="queueWait()"
      >
        <img :src="CDN.icon32('zzz')" :alt="t('map.commands.wait')" :title="t('map.commands.wait')"/>
      </button>
      <button
        v-if="shipActionStore.fleetId > 0"
        class="cmd-btn"
        @click="detachShipsFromFleet()"
      >
        <img :src="CDN.icon32('cmd_decouple')" :alt="t('map.commands.detachShips')" :title="t('map.commands.detachShips')"/>
      </button>
      <button
        class="cmd-btn"
        :class="{ 'active': shipActionStore.patrolMode && shipActionStore.patrolType === 'patrol' }"
        @click="enterPatrolMode()"
      >
        <img :src="CDN.icon32('patrol')" :alt="t('map.commands.patrol')" :title="t('map.commands.patrol')"/>
      </button>
      <button
        class="cmd-btn"
        :class="{ 'active': shipActionStore.patrolMode && shipActionStore.patrolType === 'patrolDefend' }"
        @click="enterPatrolDefendMode()"
      >
        <img :src="CDN.icon32('patrol_defend')" :alt="t('map.commands.patrolDefend')" :title="t('map.commands.patrolDefend')"/>
      </button>
    </div>

    <!-- Patrol route selector (shown when in patrol mode, hidden when adding a new route) -->
    <div v-if="shipActionStore.patrolMode && !routeEditorStore.initialized" class="flex flex-col gap-2">
      <div class="text-sm text-metal-400">
        {{ shipActionStore.patrolType === 'patrolDefend' ? t('map.commands.selectPatrolDefendRoute') : t('map.commands.selectPatrolRoute') }}
      </div>
      <div v-if="hasRoutes" class="flex items-center gap-2">
        <FormSelect
          :modelValue="shipActionStore.selectedRouteId ?? 0"
          @update:modelValue="shipActionStore.selectedRouteId = Number($event) || null"
          :options="routeOptions"
          class="flex-1"
        />
      </div>
      <div v-else class="text-sm text-metal-400 py-2">
        {{ t('map.commands.noRoutesAvailable') }}
      </div>
      <div class="flex justify-center gap-2">
        <ActionButton @click="addNewRoute">{{ t('map.route.addRoute') }}</ActionButton>
        <ActionButton
          v-if="hasRoutes"
          @click="setPatrolAndSubmit"
          :disabled="!shipActionStore.selectedRouteId"
        >{{ t('common.actions.set') }}</ActionButton>
        <DeleteButton @click="cancelPatrol">{{ t('common.actions.cancel') }}</DeleteButton>
      </div>
    </div>

    <div v-if="!isSubmitting && !shipActionStore.patrolMode" class="pt-2 flex justify-evenly gap-2">
      <ActionButton v-if="shipActionStore.actions.length>0" @click="setActions">{{ t('common.actions.set') }}</ActionButton>
      <ActionButton v-if="shipActionStore.actions.length>0" @click="undoLastAction">{{ t('map.actions.undo') }}</ActionButton>
      <DeleteButton @click="resetAction">{{ t('common.actions.cancel') }}</DeleteButton>
    </div>
  </div>
</div>
</template>

<style scoped lang="scss">
.cmd-btn {
  @apply p-1.5 m-1 rounded cursor-pointer bg-metal-600 text-metal-300 transition-all duration-100 ease-out;
  background-image: linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.1) 100%);
  box-shadow: 0 2px 0 rgba(0,0,0,0.3);
  transform: translateY(0);

  img {
    @apply w-7 h-7;
    filter: drop-shadow(1px 1px 1px rgba(0, 0, 0, 0.5));
  }

  &:hover {
    @apply brightness-110;
  }

  &:active {
    box-shadow: 0 0 0 rgba(0,0,0,0.3);
    transform: translateY(2px);
  }

  &.active {
    @apply bg-metal-700;
    box-shadow: 0 2px 0 rgba(0,0,0,0.3), inset 0 2px 4px rgba(0,0,0,0.4);
  }
}
</style>